import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/feedback/ToastHost";
import { EmptyState } from "@/components/feedback/EmptyState";
import { errorMessage } from "@/lib/errors";
import { Permission } from "@/lib/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import {
  listNotifications,
  markNotificationRead,
  readAllNotifications,
} from "@/services/api/notifications";
import type { Notification } from "@/services/api/types";
import { cn } from "@/lib/utils";

function NotificationRow({ item }: { item: Notification }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const markRead = useMutation({
    mutationFn: () => markNotificationRead(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <button
      type="button"
      onClick={() => markRead.mutate()}
      disabled={item.read_at !== null}
      className={cn(
        "block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-muted",
        item.read_at === null && "cursor-pointer",
      )}
    >
      <div className={cn("flex items-start gap-2")}>
        <span
          className={cn(
            "mt-1.5 h-2 w-2 shrink-0 rounded-full",
            item.read_at === null ? "bg-primary" : "bg-muted-foreground/30",
          )}
        />
        <div className="min-w-0">
          <p className="font-medium">{t(item.title_key, { ...item.params, defaultValue: item.title_key })}</p>
          <p className="truncate text-xs text-muted-foreground">
            {t(item.body_key, { ...item.params, defaultValue: item.body_key })}
          </p>
          {item.occurrence_count > 1 ? (
            <Badge variant="secondary" className="mt-1">
              ×{item.occurrence_count}
            </Badge>
          ) : null}
        </div>
      </div>
    </button>
  );
}

/** Header bell with the principal's unread notifications and read-all action. */
export function NotificationBell() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { any } = usePermissions();

  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: () => listNotifications(20, 0),
    enabled: any(Permission.NOTIFICATIONS_READ),
  });

  const readAll = useMutation({
    mutationFn: () => readAllNotifications(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const unread = query.data?.items.filter((item) => item.read_at === null).length ?? 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-4 w-4" />
          {unread > 0 ? (
            <Badge className="absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[10px]">
              {unread > 99 ? "99+" : unread}
            </Badge>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between pr-2">
          <DropdownMenuLabel>{t("notifications.title")}</DropdownMenuLabel>
          {unread > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => readAll.mutate()}>
              {t("notifications.readAll")}
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator />
        {query.isLoading ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t("common.loading")}
          </div>
        ) : query.data && query.data.items.length > 0 ? (
          <div className="max-h-96 overflow-y-auto">
            {query.data.items.map((item) => (
              <NotificationRow key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="px-3 py-6">
            <EmptyState title={t("notifications.empty")} />
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
